import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  CircularProgress,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@material-ui/core';
import ChevronRight from '@material-ui/icons/ChevronRight';
import FolderOpen from '@material-ui/icons/FolderOpen';
import Lock from '@material-ui/icons/Lock';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi } from '@backstage/core-plugin-api';
import { fetchApiRef, configApiRef } from '@backstage/core-plugin-api';

function getByPath(obj: unknown, path: string): unknown {
  if (!path) return obj;
  let cur: unknown = obj;
  for (const key of path.split('.')) {
    if (cur === null || cur === undefined || typeof cur !== 'object') {
      return undefined;
    }
    cur = (cur as Record<string, unknown>)[key];
  }
  return cur;
}

function normalizeBrowsePath(p: string): string {
  const t = p.trim();
  if (t === '' || t === '/') return '/';
  const withSlash = t.startsWith('/') ? t : `/${t}`;
  return withSlash.replace(/\/+$/, '') || '/';
}

/** Strip query string — we always pass `path=` ourselves (one level per view, like Akeyless UI). */
function apiBasePath(apiPath: string): string {
  const t = apiPath.trim();
  const q = t.indexOf('?');
  return (q >= 0 ? t.slice(0, q) : t).replace(/\/$/, '');
}

function initialPathFromApiPath(apiPath: string): string {
  const q = apiPath.indexOf('?');
  if (q < 0) return '/';
  const params = new URLSearchParams(apiPath.slice(q + 1));
  return normalizeBrowsePath(params.get('path') ?? '/');
}

export interface AkeylessPathPickerUiOptions {
  searchable?: boolean;
  /**
   * e.g. `/api/akeyless/items/folders` or `/api/akeyless/items/names`.
   * Optional `?path=/foo` seeds the first folder shown; `recursive` in the URL is ignored.
   */
  apiPath: string;
  arraySelector: 'folders' | 'items';
  valueSelector?: string;
  labelSelector?: string;
}

type OptionRow = {
  value: string;
  label: string;
  kind: 'folder' | 'secret';
  /** When false, path is read/list-only and cannot be chosen for create/update. */
  selectable: boolean;
};

const CACHE_TTL_MS = 2 * 60 * 1000;
const cache = new Map<
  string,
  { rows: OptionRow[]; currentPathSelectable: boolean; fetchedAt: number }
>();

export const AkeylessPathPickerField = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
  schema,
}: FieldExtensionComponentProps<string, AkeylessPathPickerUiOptions>) => {
  const fetchApi = useApi(fetchApiRef);
  const config = useApi(configApiRef);
  const opts = uiSchema?.['ui:options'] as AkeylessPathPickerUiOptions | undefined;
  const rawApiPath = opts?.apiPath?.trim() ?? '';
  const arraySelector = opts?.arraySelector ?? 'items';
  const valueSelector = opts?.valueSelector ?? 'path';
  const labelSelector = opts?.labelSelector ?? 'path';
  const searchable = opts?.searchable !== false;

  const baseFoldersUrl = useMemo(() => {
    const b = apiBasePath(rawApiPath);
    if (b.endsWith('/names')) return b.replace(/\/names$/, '/folders');
    if (b.endsWith('/folders')) return b;
    return '/api/akeyless/items/folders';
  }, [rawApiPath]);

  const baseNamesUrl = useMemo(() => {
    const b = apiBasePath(rawApiPath);
    if (b.endsWith('/names')) return b;
    if (b.endsWith('/folders')) return b.replace(/\/folders$/, '/names');
    return '/api/akeyless/items/names';
  }, [rawApiPath]);

  const [browsePath, setBrowsePath] = useState('/');
  const [rows, setRows] = useState<OptionRow[]>([]);
  const [currentPathSelectable, setCurrentPathSelectable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filterText, setFilterText] = useState('');

  const buildUrl = useCallback(
    (base: string, path: string) => {
      const backendBaseUrl = config.getString('backend.baseUrl');
      const root = backendBaseUrl.replace(/\/$/, '');
      const p = path === '/' ? '/' : path;
      return `${root}${base.startsWith('/') ? base : `/${base}`}?path=${encodeURIComponent(p)}`;
    },
    [config],
  );

  const parseRows = useCallback(
    (body: unknown, selector: 'folders' | 'items', kind: 'folder' | 'secret'): OptionRow[] => {
      const arr = getByPath(body, selector);
      if (!Array.isArray(arr)) return [];
      return arr
        .map((item: unknown) => {
          const value = String(getByPath(item, valueSelector) ?? '').trim();
          const labelRaw = getByPath(item, labelSelector);
          const label =
            labelRaw !== undefined &&
            labelRaw !== null &&
            String(labelRaw).trim() !== ''
              ? String(labelRaw)
              : value;
          const selectableRaw = getByPath(item, 'selectable');
          const selectable = selectableRaw === true;
          return { value, label, kind, selectable };
        })
        .filter(r => r.value);
    },
    [labelSelector, valueSelector],
  );

  const loadAt = useCallback(
    async (path: string) => {
      const norm = normalizeBrowsePath(path);
      setBrowsePath(norm);
      setFilterText('');

      const cacheKey = `${arraySelector}:${norm}`;
      const now = Date.now();
      const hit = cache.get(cacheKey);
      if (hit && now - hit.fetchedAt < CACHE_TTL_MS) {
        setRows(hit.rows);
        setCurrentPathSelectable(hit.currentPathSelectable);
        return;
      }

      setLoading(true);
      setLoadError(null);
      try {
        const folderUrl = buildUrl(baseFoldersUrl, norm);
        const folderRes = await fetchApi.fetch(folderUrl);
        if (!folderRes.ok) {
          const errBody = await folderRes.json().catch(() => ({}));
          throw new Error(
            typeof (errBody as { error?: string }).error === 'string'
              ? (errBody as { error: string }).error
              : `Folders HTTP ${folderRes.status}`,
          );
        }
        const folderBody = await folderRes.json();
        const folderRows = parseRows(folderBody, 'folders', 'folder');
        const pathMeta = getByPath(folderBody, 'currentPath') as
          | { selectable?: boolean }
          | undefined;
        const browseSelectable = pathMeta?.selectable === true;

        let secretRows: OptionRow[] = [];
        if (arraySelector === 'items') {
          const namesUrl = buildUrl(baseNamesUrl, norm);
          const namesRes = await fetchApi.fetch(namesUrl);
          if (!namesRes.ok) {
            const errBody = await namesRes.json().catch(() => ({}));
            throw new Error(
              typeof (errBody as { error?: string }).error === 'string'
                ? (errBody as { error: string }).error
                : `Secrets HTTP ${namesRes.status}`,
            );
          }
          const namesBody = await namesRes.json();
          secretRows = parseRows(namesBody, 'items', 'secret');
        }

        const merged = [...folderRows, ...secretRows].sort((a, b) =>
          a.label.localeCompare(b.label),
        );
        cache.set(cacheKey, {
          rows: merged,
          currentPathSelectable: browseSelectable,
          fetchedAt: Date.now(),
        });
        setRows(merged);
        setCurrentPathSelectable(browseSelectable);
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'Failed to load Akeyless paths';
        // eslint-disable-next-line no-console -- fetch failure; surface in loadError
        console.error('Akeyless drill-down fetch failed', e);
        setLoadError(message);
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [
      arraySelector,
      baseFoldersUrl,
      baseNamesUrl,
      buildUrl,
      fetchApi,
      parseRows,
    ],
  );

  useEffect(() => {
    if (!rawApiPath) return;
    void loadAt(initialPathFromApiPath(rawApiPath));
  }, [rawApiPath, loadAt]);

  const filteredRows = useMemo(() => {
    const t = filterText.trim().toLowerCase();
    if (!searchable || !t) return rows;
    return rows.filter(
      r =>
        r.label.toLowerCase().includes(t) || r.value.toLowerCase().includes(t),
    );
  }, [filterText, rows, searchable]);

  const breadcrumbParts = useMemo(() => {
    const norm = normalizeBrowsePath(browsePath);
    if (norm === '/') return [{ label: '/', path: '/' as string }];
    const segments = norm.split('/').filter(Boolean);
    const parts: { label: string; path: string }[] = [
      { label: '/', path: '/' },
    ];
    let acc = '';
    for (const seg of segments) {
      acc += `/${seg}`;
      parts.push({ label: seg, path: acc });
    }
    return parts;
  }, [browsePath]);

  const parentPath = useMemo(() => {
    const norm = normalizeBrowsePath(browsePath);
    if (norm === '/') return '/';
    const parts = norm.split('/').filter(Boolean);
    parts.pop();
    return parts.length === 0 ? '/' : `/${parts.join('/')}`;
  }, [browsePath]);

  const error = rawErrors && rawErrors.length > 0 && !formData;

  let akeylessFieldHelper: string;
  if (loadError) {
    akeylessFieldHelper = `Could not load: ${loadError}`;
  } else if (arraySelector === 'folders') {
    akeylessFieldHelper =
      'Open folders to go deeper, then “Use this folder”. The button stays disabled on paths where you only have read/list (same as browsing folders in update).';
  } else {
    akeylessFieldHelper =
      'Navigate into folders; click a static secret to select it for update. Greyed secrets are read/list-only.';
  }

  return (
    <FormControl margin="normal" required={required} error={error} fullWidth>
      <InputLabel shrink id="akeyless-path-picker-label">
        {(schema?.title as string | undefined)?.trim() || 'Akeyless path'}
      </InputLabel>
      <Box marginTop={1.5} border={1} borderColor="divider" borderRadius={4} p={1.5}>
        <Box display="flex" flexWrap="wrap" alignItems="center" mb={1}>
          <Typography variant="caption" color="textSecondary" component="span">
            Browse:&nbsp;
          </Typography>
          {breadcrumbParts.map((part, i) => (
            <Box key={part.path} display="inline-flex" alignItems="center">
              {i > 0 ? (
                <ChevronRight
                  style={{ fontSize: 16, opacity: 0.5 }}
                  viewBox="0 0 24 24"
                />
              ) : null}
              <Button
                size="small"
                color={part.path === browsePath ? 'primary' : 'default'}
                onClick={() => void loadAt(part.path)}
              >
                {part.label}
              </Button>
            </Box>
          ))}
        </Box>

        <Box
          display="flex"
          flexWrap="wrap"
          mb={1}
          style={{ gap: 8 }}
        >
          <Button
            size="small"
            variant="outlined"
            disabled={normalizeBrowsePath(browsePath) === '/'}
            onClick={() => void loadAt(parentPath)}
          >
            Up one level
          </Button>
          {arraySelector === 'folders' ? (
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={!currentPathSelectable}
              onClick={() => onChange(normalizeBrowsePath(browsePath))}
            >
              Use this folder
            </Button>
          ) : null}
          <Button size="small" onClick={() => void loadAt(browsePath)}>
            Refresh
          </Button>
        </Box>

        <TextField
          size="small"
          fullWidth
          placeholder="Filter this level…"
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          style={{ marginBottom: 8 }}
        />

        {loading ? (
          <Box py={3} display="flex" justifyContent="center">
            <CircularProgress size={32} />
          </Box>
        ) : (
          <List dense component="nav" style={{ maxHeight: 280, overflow: 'auto' }}>
            {filteredRows.length === 0 ? (
              <ListItem>
                <ListItemText
                  primary="Nothing at this level"
                  secondary={
                    arraySelector === 'items'
                      ? 'Open a subfolder or pick a static secret at this level (one folder level at a time).'
                      : 'No subfolders here — click “Use this folder”.'
                  }
                />
              </ListItem>
            ) : null}
            {filteredRows.map(row => {
              // Folders are navigation only (create + update). Grey out secrets without update.
              const readOnly = row.kind === 'secret' && !row.selectable;
              return (
              <ListItem
                key={`${row.kind}:${row.value}`}
                button
                disabled={row.kind === 'secret' && !row.selectable}
                onClick={() => {
                  if (row.kind === 'folder') {
                    void loadAt(row.value);
                  } else if (row.selectable) {
                    onChange(row.value);
                  }
                }}
                style={{
                  opacity: readOnly ? 0.45 : 1,
                }}
              >
                {row.kind === 'folder' ? (
                  <FolderOpen
                    fontSize="small"
                    style={{ marginRight: 8, opacity: 0.7 }}
                  />
                ) : (
                  <Lock
                    fontSize="small"
                    style={{ marginRight: 8, opacity: 0.7 }}
                  />
                )}
                <ListItemText
                  primary={row.label}
                  secondary={
                    readOnly
                      ? `${row.value} (read/list only)`
                      : row.value
                  }
                />
                {row.kind === 'folder' ? (
                  <ChevronRight fontSize="small" style={{ opacity: 0.5 }} />
                ) : null}
              </ListItem>
            );
            })}
          </List>
        )}
      </Box>

      {formData ? (
        <Typography
          variant="caption"
          color="textSecondary"
          display="block"
          style={{ marginTop: 8 }}
        >
          Selected: <code>{formData}</code>
        </Typography>
      ) : null}

      <FormHelperText error={Boolean(loadError)}>{akeylessFieldHelper}</FormHelperText>
    </FormControl>
  );
};
